import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import BrandMark from "@/components/BrandMark";
import { useLocale } from "@/lib/locale";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";

const formatPrice = (n) => `€${Number(n).toFixed(2).replace(".", ",")}`;

export default function CreateMoment() {
    const { id } = useParams();
    const nav = useNavigate();
    const { t } = useLocale();

    const [catalog, setCatalog] = useState(null);
    const [step, setStep] = useState(0);
    const [selection, setSelection] = useState({});
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    // Capability guard: we can't animate a face we don't have. If someone
    // navigates here directly for a presence without a photo, redirect
    // them to the editor so they're never stuck in the wizard.
    useEffect(() => {
        api.get(`/presences/${id}`)
            .then(({ data }) => {
                if (!data?.has_photo) {
                    nav(`/presence/${id}?edit=photo`, { replace: true });
                }
            })
            .catch(() => { /* if the presence call fails the wizard itself will handle errors */ });
    }, [id, nav]);

    useEffect(() => {
        api.get("/moments/catalog")
            .then(({ data }) => setCatalog(data))
            .catch(() => setErr(t("moment.err_generic")));
    }, []); // eslint-disable-line

    const groups = catalog?.groups || [];
    const group = groups[step];

    const pick = (gid, option_id) => {
        setSelection((s) => ({ ...s, [gid]: s[gid] === option_id ? undefined : option_id }));
    };

    const quote = useMemo(() => {
        if (!catalog) return { items: [], total_eur: 0 };
        const items = [];
        for (const g of catalog.groups) {
            const picked = selection[g.id];
            if (!picked) continue;
            const option = g.options.find((o) => o.id === picked);
            if (!option) continue;
            const price = option.price_eur ?? g.price_eur ?? 0;
            items.push({
                group: g.id,
                option: picked,
                label_key: option.label_key,
                price_eur: price,
            });
        }
        const total = items.reduce((s, i) => s + (i.price_eur || 0), 0);
        return { items, total_eur: Number(total.toFixed(2)) };
    }, [catalog, selection]);

    const canProceed = () => {
        if (!group) return false;
        if (group.required) return !!selection[group.id];
        return true;
    };

    const canCheckout = () => {
        if (!selection.feeling) return false;
        if (!selection.experience) return false;
        return quote.total_eur > 0;
    };

    const next = () => setStep((s) => Math.min(groups.length, s + 1));
    const prev = () => setStep((s) => Math.max(0, s - 1));

    const checkout = async (provider = "stripe") => {
        setErr("");
        if (!selection.feeling) { setErr(t("moment.err_need_feeling")); return; }
        if (!selection.experience) { setErr(t("moment.err_need_experience")); return; }
        setBusy(provider);
        try {
            const { data } = await api.post("/moments/checkout", {
                presence_id: id,
                selection,
                origin: window.location.origin,
                provider,
            });
            window.location.href = data.checkout_url;
        } catch (e) {
            setErr(e?.response?.data?.detail || t("moment.err_generic"));
            setBusy(false);
        }
    };

    if (!catalog) {
        return (
            <div className="grain min-h-screen candle-halo">
                <Header />
                <p className="max-w-3xl mx-auto px-6 pt-20 text-ink-textLow italic font-serif">…</p>
            </div>
        );
    }

    const isReviewStep = step >= groups.length;
    const headerTitle = isReviewStep ? t("moment.summary") : t(group.label_key);

    return (
        <div className="grain min-h-screen candle-halo" data-testid="create-moment-page">
            <Header minimal />
            <main className="max-w-3xl mx-auto px-6 md:px-12 pt-10 pb-44 md:pb-32 animate-fade-in">
                <div className="flex items-center gap-4 mb-6">
                    <BrandMark size={32} />
                    <p className="label-eyebrow">{t("moment.eyebrow")}</p>
                </div>
                {step === 0 && !isReviewStep && (
                    <div className="mb-12">
                        <h1 className="h-serif text-4xl md:text-5xl leading-[1.08]">
                            {t("moment.intro_a")} <span className="italic text-ink-gold">{t("moment.intro_i")}</span>
                        </h1>
                        <p className="mt-5 text-ink-textMid text-lg leading-relaxed max-w-xl">
                            {t("moment.intro_body")}
                        </p>
                    </div>
                )}

                {/* progress bar */}
                <div className="flex items-center gap-2 mb-10" data-testid="moment-progress">
                    {groups.map((g, i) => (
                        <div
                            key={g.id}
                            className={`h-[1px] flex-1 transition-colors duration-500 ${
                                i < step || (isReviewStep) ? "bg-ink-gold" : i === step ? "bg-ink-goldLo" : "bg-ink-border"
                            }`}
                        />
                    ))}
                </div>

                {!isReviewStep && (
                    <div className="animate-fade-in" key={group.id}>
                        <div className="flex items-baseline gap-3 mb-3">
                            <h2 className="h-serif text-3xl md:text-[2.4rem] leading-tight">
                                {headerTitle}
                            </h2>
                            <span className="label-eyebrow text-ink-textLow">
                                {group.required ? t("moment.required") : t("moment.optional")}
                            </span>
                        </div>
                        {/* Step subtitle — tells the user WHAT this choice
                            influences in the final moment. Always present,
                            always soft, always immediate. */}
                        {t(`moment.step${groupIndex(group.id)+1}_sub`) !== `moment.step${groupIndex(group.id)+1}_sub` && (
                            <p
                                className="text-ink-textMid text-base md:text-lg leading-relaxed italic font-serif mb-8 max-w-lg"
                                data-testid={`moment-step-sub-${group.id}`}
                            >
                                {t(`moment.step${groupIndex(group.id)+1}_sub`)}
                            </p>
                        )}

                        <div className="space-y-3" data-testid={`moment-step-${group.id}`}>
                            {group.options.map((opt) => {
                                const active = selection[group.id] === opt.id;
                                const price = opt.price_eur ?? group.price_eur ?? 0;
                                return (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() => pick(group.id, opt.id)}
                                        data-testid={`moment-option-${group.id}-${opt.id}`}
                                        className={`w-full flex items-center justify-between gap-6 p-5 rounded-sm border text-left transition-all duration-300 ${
                                            active
                                                ? "border-ink-gold bg-ink-surface shadow-[0_0_50px_-18px_rgba(200,161,101,0.4)]"
                                                : "border-ink-borderSubtle hover:border-ink-border bg-ink-surface/30"
                                        }`}
                                    >
                                        <div className="flex items-start gap-4 flex-1 min-w-0">
                                            <span className={`mt-1 w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                                                active ? "border-ink-gold bg-ink-gold" : "border-ink-border"
                                            }`}>
                                                {active && <Check size={10} className="text-ink-deep" strokeWidth={3} />}
                                            </span>
                                            <div>
                                                <p className="font-serif text-ink-textHigh text-[1.25rem] leading-snug">
                                                    {t(opt.label_key)}
                                                </p>
                                                {t(opt.label_key + "_d") !== opt.label_key + "_d" && (
                                                    <p className="text-ink-textMid text-sm italic font-serif mt-1 leading-relaxed">
                                                        {t(opt.label_key + "_d")}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {price > 0 && (
                                            <span className={`shrink-0 font-serif text-lg ${active ? "text-ink-gold" : "text-ink-textMid"}`}>
                                                {formatPrice(price)}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {isReviewStep && (
                    <div className="animate-fade-in" data-testid="moment-review">
                        <h2 className="h-serif text-3xl md:text-[2.4rem] leading-tight mb-3">{t("moment.summary")}</h2>
                        <p
                            className="text-ink-textMid text-base md:text-lg leading-relaxed italic font-serif mb-8 max-w-lg"
                            data-testid="moment-summary-sub"
                        >
                            {t("moment.summary_sub")}
                        </p>
                        <div className="border border-ink-border rounded-sm bg-ink-surface/50 p-6 md:p-8">
                            {quote.items.length === 0 ? (
                                <p className="text-ink-textMid italic">—</p>
                            ) : (
                                <ul className="divide-y divide-ink-borderSubtle">
                                    {quote.items.map((it) => (
                                        <li key={it.group} className="flex items-start justify-between py-4 gap-6" data-testid={`review-item-${it.group}`}>
                                            <div>
                                                <p className="label-eyebrow text-ink-textLow">{t(`moment.step${groupIndex(it.group)+1}_title`)}</p>
                                                <p className="font-serif text-ink-textHigh text-lg mt-1">{t(it.label_key)}</p>
                                            </div>
                                            {it.price_eur > 0 && (
                                                <span className="shrink-0 font-serif text-ink-textHigh">{formatPrice(it.price_eur)}</span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <div className="border-t border-ink-border mt-5 pt-5 flex items-center justify-between">
                                <span className="label-eyebrow">{t("moment.total")}</span>
                                <span className="font-serif text-3xl text-ink-gold" data-testid="moment-total">
                                    {formatPrice(quote.total_eur)}
                                </span>
                            </div>
                        </div>

                        {/* Trust micro-copy */}
                        <p
                            className="mt-6 text-ink-textMid text-sm md:text-[0.95rem] leading-relaxed italic font-serif text-center max-w-lg mx-auto"
                            data-testid="moment-review-trust"
                        >
                            {t("moment.review_trust")}
                        </p>

                        {/* Elegant disclaimer */}
                        <p
                            className="mt-3 text-ink-textLow text-xs md:text-[0.8rem] leading-relaxed text-center max-w-lg mx-auto"
                            data-testid="moment-review-disclaimer"
                        >
                            {t("moment.disclaimer")}
                        </p>

                        {err && <p className="mt-4 text-ink-danger italic font-serif" data-testid="moment-error">{err}</p>}
                    </div>
                )}

                {/* Navigation */}
                <div className="mt-12 pt-8 border-t border-ink-border">
                    {/* Checkout preview — explains in one soft line exactly
                        what will happen when they press the primary button.
                        Shown only on the review step, never on wizard steps. */}
                    {isReviewStep && (
                        <p
                            className="text-ink-textLow text-xs md:text-[0.85rem] italic font-serif text-center max-w-lg mx-auto mb-6 leading-relaxed"
                            data-testid="moment-checkout-next"
                        >
                            {t("moment.checkout_next")}
                        </p>
                    )}
                    <div className="flex items-center justify-between">
                    {step > 0 ? (
                        <button onClick={prev} className="btn-ghost flex items-center gap-2" data-testid="moment-back">
                            <ChevronLeft size={16} /> {t("moment.back")}
                        </button>
                    ) : <span />}
                    {!isReviewStep ? (
                        <button
                            onClick={next}
                            disabled={!canProceed()}
                            data-testid="moment-next"
                            className="btn-primary flex items-center gap-2"
                        >
                            {!group.required && !selection[group.id] ? t("moment.skip") : t("moment.next")}
                            <ChevronRight size={16} />
                        </button>
                    ) : (
                        <div className="flex flex-col sm:flex-row items-stretch gap-3">
                            <button
                                onClick={() => checkout("stripe")}
                                disabled={busy || !canCheckout()}
                                data-testid="moment-checkout"
                                className="btn-primary-glow"
                            >
                                {busy === "stripe" ? t("moment.cta_final_busy") : t("moment.cta_final")}
                            </button>
                            <button
                                onClick={() => checkout("paypal")}
                                disabled={busy || !canCheckout()}
                                data-testid="moment-checkout-paypal"
                                className="inline-flex items-center justify-center gap-2 py-3 px-5 rounded-sm border border-[#003087] bg-[#FFC439] hover:bg-[#FFB800] text-[#003087] font-semibold text-sm tracking-wide transition-colors disabled:opacity-50"
                            >
                                {busy === "paypal" ? "…" : (
                                    <>
                                        <svg width="64" height="16" viewBox="0 0 124 33" fill="none" aria-hidden="true">
                                            <path fill="#003087" d="M46.211 6.749h-6.839a.95.95 0 0 0-.939.802l-2.766 17.537a.57.57 0 0 0 .564.658h3.265a.95.95 0 0 0 .939-.803l.746-4.73a.95.95 0 0 1 .938-.803h2.165c4.505 0 7.105-2.18 7.784-6.5.306-1.89.013-3.375-.872-4.415-.972-1.142-2.696-1.746-4.985-1.746m.789 6.405c-.374 2.454-2.249 2.454-4.062 2.454h-1.032l.724-4.583a.57.57 0 0 1 .563-.481h.473c1.235 0 2.4 0 3.002.704.359.42.469 1.044.332 1.906m19.654-.079h-3.275a.57.57 0 0 0-.563.481l-.145.916-.229-.332c-.709-1.029-2.29-1.373-3.868-1.373-3.619 0-6.71 2.741-7.312 6.586-.313 1.918.132 3.752 1.22 5.031.998 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .562.66h2.95a.95.95 0 0 0 .939-.803l1.77-11.209a.568.568 0 0 0-.561-.658m-4.565 6.374c-.316 1.871-1.801 3.127-3.695 3.127-.951 0-1.711-.305-2.199-.883s-.671-1.402-.518-2.323c.296-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.499.589.7 1.418.558 2.339m22.007-6.374h-3.291a.95.95 0 0 0-.787.417l-4.539 6.686-1.924-6.425a.95.95 0 0 0-.912-.678h-3.234a.57.57 0 0 0-.541.754l3.625 10.638-3.408 4.811a.57.57 0 0 0 .465.9h3.287a.95.95 0 0 0 .781-.408l10.946-15.8a.57.57 0 0 0-.468-.895"/>
                                            <path fill="#009cde" d="M94.992 6.749h-6.84a.95.95 0 0 0-.938.802l-2.766 17.537a.57.57 0 0 0 .562.658h3.51a.665.665 0 0 0 .656-.562l.785-4.971a.95.95 0 0 1 .938-.803h2.164c4.506 0 7.105-2.18 7.785-6.5.307-1.89.012-3.375-.873-4.415-.971-1.142-2.694-1.746-4.983-1.746m.789 6.405c-.373 2.454-2.248 2.454-4.062 2.454h-1.031l.725-4.583a.57.57 0 0 1 .562-.481h.473c1.234 0 2.4 0 3.002.704.359.42.468 1.044.331 1.906m19.653-.079h-3.273a.57.57 0 0 0-.562.481l-.145.916-.23-.332c-.709-1.029-2.289-1.373-3.867-1.373-3.619 0-6.709 2.741-7.311 6.586-.312 1.918.131 3.752 1.219 5.031 1 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .564.66h2.949a.95.95 0 0 0 .938-.803l1.771-11.209a.572.572 0 0 0-.565-.658m-4.565 6.374c-.314 1.871-1.801 3.127-3.695 3.127-.949 0-1.711-.305-2.199-.883-.484-.574-.668-1.402-.518-2.323.299-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.501.589.703 1.418.558 2.339m8.426-12.219-2.807 17.858a.57.57 0 0 0 .562.658h2.822c.469 0 .867-.34.939-.803l2.768-17.536a.57.57 0 0 0-.562-.659h-3.16a.57.57 0 0 0-.562.482"/>
                                            <path fill="#003087" d="M7.266 29.154l.523-3.322-1.165-.027H1.061L4.927 1.292a.32.32 0 0 1 .314-.268h9.38c3.114 0 5.263.648 6.385 1.927.526.6.861 1.227 1.023 1.917.17.724.173 1.589.007 2.644l-.012.077v.676l.526.298a3.7 3.7 0 0 1 1.065.812c.45.513.741 1.165.864 1.938.127.795.085 1.741-.123 2.812-.24 1.232-.628 2.305-1.152 3.183a6.5 6.5 0 0 1-1.825 2c-.696.494-1.523.869-2.458 1.109-.906.236-1.939.355-3.072.355h-.73c-.522 0-1.029.188-1.427.525a2.21 2.21 0 0 0-.744 1.328l-.055.299-.924 5.855-.042.215c-.011.068-.03.102-.058.125a.16.16 0 0 1-.096.035z"/>
                                            <path fill="#009cde" d="M23.048 7.667q-.042.268-.096.55c-1.237 6.351-5.469 8.545-10.874 8.545H9.326c-.661 0-1.218.48-1.321 1.132l-1.422 9.017-.402 2.557a.703.703 0 0 0 .695.812h4.881c.578 0 1.069-.42 1.16-.99l.048-.248.919-5.832.059-.32c.09-.572.582-.992 1.16-.992h.73c4.729 0 8.431-1.92 9.513-7.476.452-2.321.218-4.259-.978-5.622a4.7 4.7 0 0 0-1.336-1.027z"/>
                                            <path fill="#012169" d="M21.754 7.151a10 10 0 0 0-1.203-.267 15 15 0 0 0-2.426-.177h-7.352a1.17 1.17 0 0 0-1.159.992L8.05 17.605l-.045.289a1.336 1.336 0 0 1 1.321-1.132h2.752c5.405 0 9.637-2.195 10.874-8.545q.057-.282.096-.55a6.6 6.6 0 0 0-1.017-.429 9 9 0 0 0-.277-.087"/>
                                            <path fill="#003087" d="M9.614 7.699a1.17 1.17 0 0 1 1.159-.991h7.352c.871 0 1.684.057 2.426.177a10 10 0 0 1 1.481.353c.365.121.704.264 1.017.429.368-2.347-.003-3.945-1.272-5.392C20.378 1.282 17.853.5 14.622.5h-9.38c-.66 0-1.223.48-1.325 1.133L.01 26.83a.806.806 0 0 0 .795.932h5.791l1.454-9.225z"/>
                                        </svg>
                                        <span>Paga con PayPal</span>
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                    </div>
                </div>
            </main>
        </div>
    );
}

function groupIndex(id) {
    return ["feeling", "scene", "style", "experience"].indexOf(id);
}
